import { Button, Dropdown, Form, Input, Menu, message, Modal, Space, Tooltip } from "antd";
import { MoreOutlined, ShopOutlined, WarningOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { getPermissions } from "../../../../services/authService";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import _t from "../../../../lang/translate";
import moment from "moment";
import { currentWarning, warningTarget } from "./catalogWarnings";
import { getCatalogWarningHistory, updateCatalogWarning } from "./service";
import { Advert, CatalogWarning, CatalogModeration, WarningHistoryEntry, WarningTarget } from "./types";

type Props = {
  advert: Advert;
  moderation: CatalogModeration;
  onChanged: (warning: CatalogWarning) => void;
};

export function WarningIndicators({ advert, moderation }: Pick<Props, "advert" | "moderation">) {
  const adWarning = currentWarning(advert, "ad", moderation);
  const merchantWarning = currentWarning(advert, "merchant", moderation);
  return <Space size={6}>
    {adWarning?.active && (
      <Tooltip title={`${_t("catalog_ad_warning")}: ${adWarning.reason}`}>
        <WarningOutlined aria-label={_t("catalog_ad_warning")} style={{ color: "#d48806", fontSize: 16 }} />
      </Tooltip>
    )}
    {merchantWarning?.active && (
      <Tooltip title={`${_t("catalog_merchant_warning")}: ${merchantWarning.reason}`}>
        <ShopOutlined aria-label={_t("catalog_merchant_warning")} style={{ color: "#cf1322", fontSize: 16 }} />
      </Tooltip>
    )}
  </Space>;
}

export default function MarketPlaceWarnings({ advert, moderation, onChanged }: Props) {
  const permissions = getPermissions();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<{ target: WarningTarget; active: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<WarningHistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyType, setHistoryType] = useState<WarningTarget["type"]>("ad");
  const historyRequest = useRef(0);
  useEffect(() => () => { historyRequest.current++; }, []);

  if (!permissions.includes("ad_catalog.manage_ad_warnings")) return null;

  function edit(type: WarningTarget["type"], active: boolean) {
    const target = warningTarget(advert, type);
    if (!target) return;
    setEditing({ target, active });
    form.setFieldsValue({ reason: active ? currentWarning(advert, type, moderation)?.reason ?? "" : "" });
  }

  async function save() {
    let values: { reason: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!editing || saving) return;
    setSaving(true);
    try {
      const response = await updateCatalogWarning(editing.target, editing.active, values.reason.trim());
      onChanged(response.data.data);
      setEditing(null);
      message.success(_t("catalog_warning_saved"));
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setSaving(false);
    }
  }

  async function showHistory(type: WarningTarget["type"]) {
    const target = warningTarget(advert, type);
    if (!target) return;
    const requestId = ++historyRequest.current;
    setHistoryType(type);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const response = await getCatalogWarningHistory(target);
      if (requestId === historyRequest.current) setHistory(response.data.data);
    } catch (error) {
      if (requestId !== historyRequest.current) return;
      setHistory(null);
      renderErrorMessage(error);
    } finally {
      if (requestId === historyRequest.current) setHistoryLoading(false);
    }
  }

  const items = (["ad", "merchant"] as const).filter(type => !!warningTarget(advert, type)).map(type => {
    const target = warningTarget(advert, type)!;
    const active = !!currentWarning(advert, type, moderation)?.active;
    const canManage = permissions.includes(type === "ad" ? "ad_catalog.manage_ad_warnings" : "ad_catalog.manage_merchant_warnings");
    const children = [];
    if (canManage) {
      children.push({
        key: `${type}-edit`,
        label: type === "ad" ? _t(active ? "catalog_edit_ad_warning" : "catalog_warning_ad") : _t(active ? "catalog_edit_merchant_warning" : "catalog_warning_merchant"),
        onClick: () => edit(type, true),
      });
      if (active) children.push({
        key: `${type}-remove`,
        label: _t(type === "ad" ? "catalog_remove_ad_warning" : "catalog_remove_merchant_warning"),
        onClick: () => edit(type, false),
      });
    }
    children.push({
      key: `${type}-history`,
      label: _t(type === "ad" ? "catalog_ad_history" : "catalog_merchant_history"),
      onClick: () => showHistory(type),
    });
    return { type: "group" as const, key: type, label: type === "ad" ? advert.name : target.sellerName, children };
  });
  const menu = <Menu items={items} />;

  return <span onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()}>
    <Space size={4}>
      <WarningIndicators advert={advert} moderation={moderation} />
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button type="text" size="small" aria-label={_t("catalog_warning_actions")} icon={<MoreOutlined />} />
      </Dropdown>
    </Space>
    <Modal visible={!!editing} destroyOnClose confirmLoading={saving} onOk={save}
      onCancel={() => !saving && setEditing(null)}
      title={editing?.target.type === "merchant"
        ? _t(editing.active ? "catalog_warning_merchant" : "catalog_remove_merchant_warning")
        : _t(editing?.active ? "catalog_warning_ad" : "catalog_remove_ad_warning")}>
      <p>{editing?.target.type === "merchant" ? editing.target.sellerName : advert.name}</p>
      {editing?.target.type === "merchant" && <p>{_t("catalog_merchant_scope")}</p>}
      <Form form={form} layout="vertical">
        <Form.Item name="reason" label={_t("catalog_warning_reason")}
          rules={[{ required: true, whitespace: true, message: _t("catalog_warning_reason_required") }]}>
          <Input.TextArea rows={4} maxLength={2000} disabled={saving} />
        </Form.Item>
      </Form>
    </Modal>
    <Modal visible={history !== null} onCancel={() => { historyRequest.current++; setHistory(null); }} footer={null}
      title={_t(historyType === "ad" ? "catalog_ad_history" : "catalog_merchant_history")}>
      {historyLoading ? _t("load") : history?.length ? history.map(entry => (
        <div key={entry.id} className="mb-1">
          <strong>{_t(entry.active ? "catalog_warning_set" : "catalog_warning_removed")}</strong>
          <div>{entry.actor} · {moment(entry.createdAt).format("DD-MM-YYYY HH:mm")}</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{entry.reason}</div>
        </div>
      )) : _t("catalog_no_history")}
    </Modal>
  </span>;
}
